import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { errorLogger } from '~/src/lib/error-logger';
import { useAuth } from '~/src/components/auth/AuthProvider';

type FallbackRender = (info: { error: Error | undefined; errorInfo: ErrorInfo | undefined; errorId: string }) => ReactNode;

interface Props {
  children?: ReactNode;
  fallback?: ReactNode | FallbackRender;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
    };
  }

  private static readonly CHUNK_ERROR_PATTERN =
    /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|Importing a module script failed/i;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('🔥 Error Boundary caught an error:', error);
    console.error('📋 Error Info:', errorInfo);

    // If this is a stale chunk error, attempt an automatic reload
    const msg = error.message ?? '';
    if (ErrorBoundary.CHUNK_ERROR_PATTERN.test(msg)) {
      const key = 'eb-chunk-reload';
      try {
        const retried = window.sessionStorage.getItem(key);
        if (!retried) {
          window.sessionStorage.setItem(key, 'true');
          console.warn('🔄 Stale chunk detected — reloading page…');
          window.location.reload();
          return; // don't log — the page is about to navigate
        }
      } catch {
        // sessionStorage unavailable — fall through to normal error handling
      }
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Log error for monitoring (in production, this could send to a service)
    this.logError(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private logError = async (error: Error, errorInfo: ErrorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId: this.state.errorId,
    };

    // Log to console for development
    if (import.meta.env?.DEV) {
      console.group('🐛 Error Boundary - Development Info');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Full Context:', errorData);
      console.groupEnd();
    }

    // Log to database for monitoring
    try {
      await errorLogger.logError(error, {
        errorId: this.state.errorId,
        componentName: 'ErrorBoundary',
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        errorType: 'javascript_error',
        severity: 'high'
      });
    } catch (logError) {
      console.error('Failed to log error to database:', logError);
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: '',
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return (this.props.fallback as FallbackRender)({
            error: this.state.error,
            errorInfo: this.state.errorInfo,
            errorId: this.state.errorId,
          });
        }
        return this.props.fallback as ReactNode;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-16 w-16 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-red-700">Something went wrong</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-gray-600">
                We're sorry, but something unexpected happened. This error has been logged 
                and we'll look into it.
              </p>

              {this.state.error?.message && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 text-center">
                  <p className="font-semibold">Error Details</p>
                  <p className="mt-1 break-words">{this.state.error.message}</p>
                </div>
              )}
              
              {/* Error details in development */}
              {import.meta.env?.DEV && this.state.error && (
                <details className="bg-gray-100 p-4 rounded-md text-sm">
                  <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="font-mono text-xs space-y-2">
                    <div>
                      <strong>Message:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="text-center space-y-3">
                <p className="text-sm text-gray-500">
                  Error ID: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{this.state.errorId}</code>
                </p>
                
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button 
                    onClick={this.handleRetry} 
                    variant="default"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                  
                  <Button 
                    onClick={this.handleReload} 
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reload Page
                  </Button>
                  
                  <Button 
                    onClick={this.handleGoHome} 
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Home className="h-4 w-4" />
                    Go Home
                  </Button>
                </div>
              </div>

              {/* Support contact in production */}
              {import.meta.env?.PROD && (
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    If this problem persists, please contact{' '}
                    <a 
                      href="mailto:support@control-exchange.com" 
                      className="text-blue-600 hover:underline"
                    >
                      support@control-exchange.com
                    </a>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for functional components to handle errors
export const useErrorHandler = () => {
  return (error: Error, errorInfo?: ErrorInfo) => {
    console.error('🔥 useErrorHandler caught error:', error);
    
    // In a real app, you might want to show a toast or modal
    // For now, we'll just log it
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      ...errorInfo,
    };
    
    if (import.meta.env?.PROD) {
      console.error('🚨 Handled Error:', errorData);
    }
  };
};
